import React, { useCallback, useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const CHECK_LABEL = {
  frameworks_present: "Assessment frameworks",
  overlays_present: "SAP + Salesforce blueprint overlays",
  resources_present: "Downloadable resources",
  toolkits_present: "Toolkit items",
};

const COUNT_LABEL = {
  frameworks: "Frameworks",
  csi_moves: "CSI moves",
  resources: "Resources",
  toolkit_items: "Toolkit items",
  erp_overlay_sap: "SAP overlay mappings",
  erp_overlay_salesforce: "Salesforce overlay mappings",
  case_studies: "Case studies",
  insights: "Insight articles",
  leads: "Leads",
  engagements: "Engagements",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function EnvironmentHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ops/environment-health");
      setData(data);
    } catch (e) {
      toast.error("Could not load environment health");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="ek-admin space-y-6 max-w-4xl" data-testid="environment-health-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="ek-heading text-xl flex items-center gap-2"><Activity size={20} /> Environment health</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            Post-deploy provisioning check — confirms a fresh environment self-seeded correctly. Read-only; safe to poll after any deploy.
          </p>
        </div>
        <button onClick={load} disabled={loading} data-testid="env-health-refresh"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] border ek-line rounded-lg ek-text-soft hover:bg-zinc-50 disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center gap-2 ek-text-muted text-sm py-10" data-testid="env-health-loading">
          <Loader2 size={16} className="animate-spin" /> Loading…
        </div>
      ) : data ? (
        <>
          <div className={`rounded-xl border p-4 flex items-center gap-3 ${data.healthy ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
               data-testid="env-health-banner">
            {data.healthy
              ? <CheckCircle2 size={22} className="text-emerald-600" />
              : <AlertTriangle size={22} className="text-amber-600" />}
            <div>
              <div className="font-semibold text-[14px] ek-text" data-testid="env-health-status">
                {data.healthy ? "Environment provisioned & healthy" : "Provisioning incomplete — review below"}
              </div>
              <div className="ek-text-muted text-[12px]">Checked {fmt(data.generated_at)}</div>
            </div>
          </div>

          <div>
            <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">Readiness checks</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(data.checks || {}).map(([k, ok]) => (
                <div key={k} data-testid={`env-check-${k}`}
                     className="flex items-center gap-2 border ek-line rounded-lg px-3 py-2 ek-surface">
                  {ok ? <CheckCircle2 size={15} className="text-emerald-600" /> : <AlertTriangle size={15} className="text-amber-600" />}
                  <span className="text-[13px] ek-text-soft">{CHECK_LABEL[k] || k}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">SME verification coverage</h3>
            <p className="ek-text-muted text-[12px] mb-2 max-w-2xl">How much of the illustrative methodology IP an accredited SME has signed off — the defensible model-risk story. Unverified content stays watermarked "advisory only".</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="env-verification">
              {[
                { key: "erp_overlay", label: "ERP / Salesforce blueprint overlays" },
                { key: "delivery_tom", label: "Delivery TOM catalogue items" },
              ].map(({ key, label }) => {
                const v = data.verification?.[key] || { verified: 0, total: 0, pct: 0 };
                const done = v.total > 0 && v.verified >= v.total;
                return (
                  <div key={key} data-testid={`env-verify-${key}`} className="border ek-line rounded-lg px-3 py-3 ek-surface">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-[13px] ek-text-soft">{label}</span>
                      <span className={`text-[12px] font-semibold ${done ? "text-emerald-600" : "text-amber-600"}`} data-testid={`env-verify-${key}-pct`}>
                        {v.verified}/{v.total} · {v.pct}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full ek-surface-2 overflow-hidden">
                      <div className={`h-2 rounded-full ${done ? "bg-emerald-500" : "bg-[#7737F6]"}`} style={{ width: `${v.pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div>
            <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">Live content counts</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {Object.entries(data.counts || {}).map(([k, v]) => (
                <div key={k} data-testid={`env-count-${k}`} className="border ek-line rounded-lg px-3 py-2 ek-surface-2">
                  <div className="text-xl font-semibold ek-text">{v}</div>
                  <div className="ek-text-muted text-[11px] leading-tight mt-0.5">{COUNT_LABEL[k] || k}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="border ek-line rounded-xl p-4 ek-surface">
            <h3 className="ek-text-soft text-[12px] uppercase tracking-[0.14em] mb-2">One-time seed state</h3>
            <div className="flex flex-wrap gap-1.5" data-testid="env-seed-done">
              {(data.seed_state?.done || []).length
                ? data.seed_state.done.map((s) => (
                    <span key={s} className="text-[11px] border border-emerald-300 bg-emerald-50 text-emerald-700 rounded px-2 py-0.5">{s}</span>
                  ))
                : <span className="ek-text-muted text-[12px]">Not yet recorded</span>}
            </div>
            <div className="ek-text-muted text-[11px] mt-2">Last updated {fmt(data.seed_state?.updated_at)}</div>
            {(data.build?.release || data.build?.commit) && (
              <div className="ek-text-muted text-[11px] mt-2" data-testid="env-build">
                Build: {data.build.release || "—"} {data.build.commit ? `· ${data.build.commit.slice(0, 8)}` : ""}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
